// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Every piece of domain jargon this site uses, defined for someone who has never read an air
// quality report. Terms are attached to the UI with `.glossary-link` spans carrying a
// `data-term` attribute; a single delegated click handler looks the term up and positions a
// popover. Click away or press Escape to dismiss.

export interface GlossaryEntry {
  term: string;
  short: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  'pm2.5': {
    term: 'PM2.5',
    short: 'Particles under 2.5 micrometres across.',
    body:
      'Airborne particles smaller than 2.5 micrometres — roughly a thirtieth the width of a human hair. They are small enough to travel past the nose and throat, deep into the lungs, and the smallest of them cross into the bloodstream. Smoke from bushfires and wood heaters is overwhelmingly PM2.5. It is measured in micrograms per cubic metre of air (µg/m³).',
  },
  pm10: {
    term: 'PM10',
    short: 'Particles under 10 micrometres across.',
    body:
      'Airborne particles smaller than 10 micrometres. This measure includes all of PM2.5 plus coarser material — wind-blown soil, sea salt, pollen, crushed rock, brake and tyre wear. Dust storms show up strongly in PM10 and comparatively weakly in PM2.5, which is how the two together tell you whether you are looking at dust or at smoke.',
  },
  'fine fraction': {
    term: 'Fine fraction',
    short: 'PM2.5 divided by PM10 — what kind of particle this is.',
    body:
      'The share of measured particle mass that is fine. Combustion makes fine particles, so smoke typically runs above 0.6. Mechanical processes make coarse ones, so a dust storm typically runs below 0.35. This site uses the ratio to label an episode, and always shows you the number so you can disagree with the label.',
  },
  'µg/m³': {
    term: 'µg/m³',
    short: 'Micrograms of particle mass in a cubic metre of air.',
    body:
      'The standard unit for particle pollution: how many millionths of a gram of particle matter are suspended in each cubic metre of air. A clean coastal hour is around 3. The Australian daily standard is 25. The worst single hour in this archive is over 13,000.',
  },
  nepm: {
    term: 'AAQ NEPM',
    short: 'The national air quality standard.',
    body:
      'The National Environment Protection (Ambient Air Quality) Measure — the instrument that sets Australia’s air quality standards. The current PM2.5 standards are 25 µg/m³ averaged over a day and 8 µg/m³ averaged over a year, with zero allowed exceedances. Separate 2025 "goals" of 20 and 7 exist in a different schedule; they are goals, not standards, and are excluded from compliance assessment.',
  },
  'exceptional event': {
    term: 'Exceptional event',
    short: 'A day officially excluded from compliance.',
    body:
      'The NEPM allows a jurisdiction to exclude a day from compliance reporting when the pollution came from a bushfire, an authorised hazard-reduction burn, or a continental-scale dust storm. So the standard allows zero exceedance days and, in the same instrument, sets aside exactly the days that pollute most. Those determinations are published in annual PDF reports, not in any data feed, so this site does not attempt to reproduce them — every exceedance count here is as measured.',
  },
  aqc: {
    term: 'Air Quality Category (AQC)',
    short: 'What New South Wales publishes instead of an index.',
    body:
      'In November 2020 New South Wales abolished its numeric Air Quality Index and replaced it with five named categories — Good, Fair, Poor, Very Poor and Extremely Poor — with no index number at all. Queensland still publishes a numeric index. They are not the same scale and they do not always give the same word for the same air.',
  },
  aqi: {
    term: 'Air Quality Index (AQI)',
    short: 'Concentration divided by the standard, times 100.',
    body:
      'The classic Australian index: an AQI of 100 means the air is sitting exactly at the standard. Queensland still serves it. New South Wales abolished it in 2020. The United States uses a different formula with different breakpoints entirely, so a number quoted from a global app is usually not comparable to an Australian one.',
  },
  who: {
    term: 'WHO guideline',
    short: 'The World Health Organization’s health-based value.',
    body:
      'The WHO’s 2021 Global Air Quality Guidelines put the health-protective level for PM2.5 at 15 µg/m³ over a day and 5 µg/m³ over a year. Australia’s standards are 25 and 8 — that is, Australia’s daily standard is about 1.7 times the level the WHO considers protective. Guidelines are not law anywhere; they are what the evidence supports.',
  },
  episode: {
    term: 'Episode',
    short: 'A run of hours when the air was measurably bad.',
    body:
      'This site defines an episode as a run of at least three hours in which at least one monitoring cluster exceeded both an absolute floor of 25 µg/m³ of PM2.5 — the whole daily standard, in a single hour — and its own 95th percentile for that month of the year. The per-station, per-month baseline is what stops a town with heavy winter wood-heater use registering every cold evening as an event.',
  },
  cluster: {
    term: 'Station cluster',
    short: 'Monitors within 2 km of each other, treated as one place.',
    body:
      'Several monitoring stations sit within metres of each other — a replacement instrument recorded under a slightly different name, for instance. Left separate, a rule of the form "at least two neighbouring stations agree" would let one instrument corroborate itself and turn a fault into a regional event. Stations within 2 km in the same jurisdiction are collapsed into one cluster before anything is detected.',
  },
  suspect: {
    term: 'Suspect episode',
    short: 'Detected at one place only, so it may be an instrument fault.',
    body:
      'An episode that no neighbouring cluster corroborated at its peak hour. It may be perfectly real — a single valley under an inversion, a fire close to one town — or it may be a faulty sensor. This site publishes them, marked, rather than deleting them, because which of the two it is is itself worth knowing.',
  },
  capture: {
    term: 'Data capture',
    short: 'The share of hours in a year that were actually measured.',
    body:
      'Instruments fail, get calibrated, and get taken away for repair. A year with 70% capture is missing about 2,600 hours. This site refuses to compute a station-year statistic below 75% capture, and the headline is recomputed at 95% to check it does not depend on the threshold.',
  },
  inversion: {
    term: 'Temperature inversion',
    short: 'A lid of warm air that traps pollution near the ground.',
    body:
      'On still, clear winter nights the ground cools faster than the air above it, so a layer of warmer air sits on top of colder air and stops it rising. Anything emitted underneath — wood-heater smoke, most of all — accumulates instead of dispersing. It is why a small country town on a cold night can register worse air than a capital city.',
  },
  'reference monitor': {
    term: 'Reference monitor',
    short: 'A regulatory-grade instrument, as opposed to a low-cost sensor.',
    body:
      'Instruments that meet the Australian Standard for measuring a pollutant, which is what compliance assessment requires. Queensland also runs a network of low-cost smoke sensors that report a quantity with the identical name, abbreviation and units. This site keeps them apart: everything analysed here is reference-grade.',
  },
  'pollution rose': {
    term: 'Pollution rose',
    short: 'Concentration arranged by the wind direction it arrived on.',
    body:
      'A wind rose shows where the wind blows from. A pollution rose shades each direction and speed by how dirty the air was when the wind came from there. If one bearing is consistently dirty, the source is upwind of you; if the worst air arrives on calm hours instead, it is being made where you are.',
  },
};

export function glossaryLink(term: string, text?: string): string {
  const entry = GLOSSARY[term.toLowerCase()];
  if (!entry) return text ?? term;
  return `<span class="glossary-link" data-term="${term.toLowerCase()}" role="button" tabindex="0" aria-label="What is ${entry.term}?">${
    text ?? entry.term
  }<span class="glossary-mark" aria-hidden="true">ℹ</span></span>`;
}
